import {
  AppealStatus,
  WriteOffStatus,
  WriteOffCategory,
  WriteOffType,
  GroupByOption,
  WriteOffSortByOption,
} from '@repo/types';
import { QueryStringParams } from '@xtnd/web/shared';

/**
 * ActionDialogMode type for write-off action dialogs.
 * Uses WriteOffStatus enum values for status-changing actions, plus 'note' for comment-only updates.
 *
 * - WriteOffStatus.APPROVED: Approve the write-off
 * - WriteOffStatus.REJECTED: Reject the write-off
 * - WriteOffStatus.ARCHIVED: Mark as processed/archived
 * - 'note': Add comment without status change
 */
export type WRITE_OFF_SORT_BY = `${WriteOffSortByOption}`;
export type ActionDialogMode =
  | WriteOffStatus.APPROVED
  | WriteOffStatus.REJECTED
  | WriteOffStatus.ARCHIVED
  | WriteOffStatus.ESCALATED
  | 'note';
type GROUP_BY = GroupByOption.NONE | GroupByOption.CLIENT;

export type WriteOffGroupByOption = {
  label: string;
  value: GROUP_BY;
};

export const WriteOffGroupByOptions: WriteOffGroupByOption[] = [
  { label: 'No Grouping', value: GroupByOption.NONE },
  { label: 'Group by Client', value: GroupByOption.CLIENT },
];

export interface ArWriteOffFilter {
  groupBy: GROUP_BY;
  daysPastDue: number;
  search?: string;
  appealStatus?: AppealStatus[];
  statusExplicit?: boolean;
  invoiceNumber?: string;
  category?: WriteOffCategory;
  matterDisplayNumber?: string;
  entityId?: string;
}
export interface WriteOffParams extends QueryStringParams {
  filters?: ArWriteOffFilter;
}

/**
 * Schemaless WriteOff type that accepts any properties from the API response.
 * Known properties are defined for type safety, but additional properties are allowed.
 */
export type WriteOff = {
  invoiceId: string;
  invoiceNumber?: string;
  clientName?: string;
  matterDisplayNumber: string;
  total?: number;
  paid?: number;
  creditsIssued?: number;
  invoiceDueAt?: string;
  writeOffStatus: WriteOffStatus | WriteOffStatus[];
  paymentSummaryUrl?: string;
  comment?: string;
  updatedBy?: string;
  updatedAt?: string;
  createdBy?: string;
  createdAt?: string;
  actionBy?: string;
  actionDate?: string;
  category?: WriteOffCategory;
  appealStatus?: AppealStatus;
  amount?: number;
  outstandingBalance?: number;
  writeOffAmount?: number;
  appealCollectedAmount?: number;
  totalFeesCut?: number;
  totalExpensesCut?: number;
  writeOff?: {
    category?: WriteOffCategory;
    amount?: number;
    matterDisplayNumber?: number;
    matterDescription?: number;
    createdBy?: string;
    updatedBy?: string;
    createdAt?: string;
    updatedAt?: string;
    daysPastDue?: number;
    createdByName?: string;
    updatedByName?: string;
    feesCut?: number;
    expensesCut?: number;
    type?: WriteOffType;
    finalAppeal?: boolean;
  };
  totalProposedWriteOff?: number;
  totalProposedFeesCut?: number;
  totalProposedExpensesCut?: number;
  totalDiscountWriteOffs?: number;
  writeOffs?: WriteOffItem[];
};

export interface UpdateWriteOffPayload {
  writeOffId: string;
  invoiceId: string;
  category?: WriteOffCategory;
  comment?: string;
  status?: WriteOffStatus;
  appealStatus?: AppealStatus;
  amount?: number;
  feesCut?: number;
  approvedFeesCut?: number;
}

export interface WriteOffGroupByClient {
  clientId: string;
  clientName: string;
  clientEmail?: string;
  totalWriteOffAmount: number;
  total: number;
  paid: number;
  creditsIssued: number;
  outstandingBalance: number;
  maxDaysPastDue: number;
  numOfInvoices: number;
}

export interface WriteOffItem {
  writeOffId: string;
  invoiceId: string;
  appealId?: string;
  type?: WriteOffType;
  category: WriteOffCategory;
  status: WriteOffStatus;
  amount: number;
  createdByName: string;
  updatedByName: string;
  invoice?: WriteOff;
  feesCut: number;
  expensesCut: number;
  comment?: string;
  fromAppealStatus?: AppealStatus;
  isWriteOffRemaining?: boolean;
}

export type FlatWriteOffItem = WriteOffItem & {
  invoiceNumber?: string;
  clientName?: string;
  matterDisplayNumber: string;
  total?: number;
  paid?: number;
  creditsIssued?: number;
  writeOffStatus: WriteOffStatus;
  // Included from WriteOffItem but listed for clarity on flat row usage
  type?: WriteOffType;
  category: WriteOffCategory;
  amount: number; // writeOffAmount
  feesCut: number;
  expensesCut: number;
  _parentInvoice?: WriteOff; // Reference to parent
};

// ============================================================================
// Types for Write-Off Tab Components (migrated from shared/WriteOffColumns.tsx)
// ============================================================================

/** Tab type identifier */
export type WriteOffTabType =
  | 'proposed'
  | 'approved'
  | 'processed'
  | 'rejected'
  | 'escalation'
  | 'all';

/** Action menu item configuration */
export type ActionMenuItem = {
  icon: string;
  iconColor: string;
  label: string;
  dialogId?: string;
  onClick?: (row: WriteOffItem) => void;
  showDividerBefore?: boolean;
  shouldShow?: (row: WriteOff | WriteOffItem | FlatWriteOffItem) => boolean;
};

/** Dialog state and handlers */
export interface DialogState {
  isOpen: boolean;
  selectedWriteOff: WriteOffItem | null;
  open: (row: WriteOffItem) => void;
  close: () => void;
}

/** Configuration for action column in write-off tables */
export type ActionColumnConfig = {
  isAdmin: boolean;
  mutatingRows: Set<string>;
  actionsMenuOpen: string | null;
  setActionsMenuOpen: (value: string | null) => void;
  setMenuTriggerElement: (element: HTMLElement | null) => void;
  menuTriggerElement: HTMLElement | null;
  actions: ActionMenuItem[];
};

/** Configuration for activity column */
export type ActivityColumnConfig = {
  onOpenActivity?: (row: WriteOff) => void;
};

/** Configuration for write-off columns */
export type WriteOffColumnsConfig = ActionColumnConfig & {
  /** Show split invoice indicator in Invoice column (Proposed only) */
  showSplitInvoice?: boolean;
  /** Activity column configuration for opening Activity drawer */
  activityConfig?: ActivityColumnConfig;
};

export const statusMap: Record<WriteOffTabType, WriteOffStatus | WriteOffStatus[]> = {
  proposed: WriteOffStatus.PENDING,
  approved: WriteOffStatus.APPROVED,
  rejected: WriteOffStatus.REJECTED,
  processed: WriteOffStatus.ARCHIVED,
  escalation: WriteOffStatus.ESCALATED,
  all: [
    WriteOffStatus.PENDING,
    WriteOffStatus.ESCALATED,
    WriteOffStatus.APPROVED,
    WriteOffStatus.REJECTED,
    WriteOffStatus.ARCHIVED,
    WriteOffStatus.CLOSED,
  ],
};
